import React, { useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const BOTTOM_THRESHOLD = 24;

const normalizeLog = (log) => {
  if (typeof log === 'string') return { message: log, level: 'info' };
  return {
    message: log && typeof log.message === 'string' ? log.message : '',
    level: log && log.level ? log.level : 'info',
  };
};

const LogsPanel = ({ logs }) => {
  const bodyRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const scrollToBottom = () => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const handleScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD;
  };

  useLayoutEffect(() => {
    if (stickToBottomRef.current) scrollToBottom();
  }, [logs]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (stickToBottomRef.current) scrollToBottom();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="vibe-terminal" aria-label="Logs">
      <div className="vibe-terminal-header">
        <i className="wb webconnect-terminal" aria-hidden="true" />
        <span>Logs</span>
      </div>
      <div className="vibe-terminal-body" ref={bodyRef} onScroll={handleScroll}>
        {logs.length ? (
          logs.map((log, index) => {
            const normalizedLog = normalizeLog(log);
            const levelClass = ['info', 'warn', 'error'].includes(normalizedLog.level)
              ? `is-${normalizedLog.level}`
              : 'is-info';
            return (
              <div key={index} className={`vibe-terminal-line ${levelClass}`}>
                {normalizedLog.message}
              </div>
            );
          })
        ) : (
          <div className="vibe-terminal-line is-info">No logs yet.</div>
        )}
      </div>
    </section>
  );
};

LogsPanel.propTypes = {
  logs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        message: PropTypes.string,
        level: PropTypes.oneOf(['info', 'warn', 'error']),
      }),
    ]),
  ),
};

LogsPanel.defaultProps = {
  logs: [],
};

export default LogsPanel;
